import type { GrowContextInput } from "@/lib/grow-assistant-context";
import { GROW_NOTE_MAX_ITEMS, RULE_LINE, type GrowNoteCount, type GrowNoteParts } from "@/lib/grow-note";
import { questionnaireState } from "@/lib/self-assessment";
import { escapeMarkers } from "@/pages/studio/notebook-markdown";

// The Studio notebook a Grow improve answer is saved as (spark#1218). The chat answer is the short
// version; the notebook is the full one, in the order spark#1218 "Reihenfolge im Notizbuch" sets: the
// definition, the growth areas, the examples, the goals, then the summary and the works it drew on, with a
// rule between each of these parts and none inside them. It is created at once as its own fallback, from
// what spark has itself (the definition and the growth areas). The GROWTH assistant then writes the note's own parts
// in one request, and they complete it in place (spark#1218, "Ablauf beim Speichern"; agent-service#133,
// "The note's own parts, written at save").

/** The part of the notebook spark has itself, captured at the improve click (spark#1218). */
export interface GrowNotebookDraft {
  powerName: string;
  /** The platform definition the click registered in `<def>`. */
  definition: string | null;
  /** The app's banded statement for the score; null below the completeness bar. */
  assessment: string | null;
  /** The weakest statements as frozen at the click, weakest first, in full; empty below the bar. */
  growthAreas: readonly string[];
  /** The save hint the click sent in `<labels>`, so the notebook does not keep it; null when none went. */
  saveHint: string | null;
}

/**
 * The notebook's own part of what the click sends. The same completeness test as `growHeadXml`:
 * below the bar the assessment and the statements stay out of the notebook as they stay out of the
 * turn. The statements are not cut at the head's 160 characters, since the notebook has the room.
 */
export function growNotebookDraft(input: GrowContextInput): GrowNotebookDraft {
  const complete = questionnaireState(input.answered, input.total) === "complete";
  return {
    powerName: input.powerName,
    definition: input.definition,
    assessment: complete ? (input.assessment ?? null) : null,
    growthAreas: complete ? input.weak.map((w) => w.text) : [],
    saveHint: input.labels?.saveHint ?? null,
  };
}

/** The notebook's fixed texts in the app's language, from i18n. */
export interface GrowNotebookHeadings {
  definition: string;
  /** Opens the growth areas, before the banded statement: the improve answer's own lead-in. */
  leadIn: string;
  growthAreas: string;
  /** Heads the one sentence on which example to start with. */
  summary: string;
  /** Stands where the examples go while the note has none: its own parts could not be written. */
  notWritten: string;
  /** Heads the titles of the reference works the note's own parts drew on. */
  sources: string;
}

/**
 * `text` without the save hint at its end, as spark sent it or wrapped in bold or italics: the
 * wrappings agent-service's GrowthAnswerCheck accepts as the hint placed verbatim.
 */
function withoutSaveHint(text: string, saveHint: string | null): string {
  const kept = text.trim();
  const hint = saveHint?.trim();
  if (!hint) return kept;
  const copy = [`**${hint}**`, `*${hint}*`, `_${hint}_`, hint].find((candidate) => kept.endsWith(candidate));
  return copy ? kept.slice(0, kept.length - copy.length).trim() : kept;
}

/**
 * The improve answer split into its examples and its summary, at the LAST rule line. Without a rule
 * the whole answer counts as the examples and the summary is empty: the only text ever dropped is a
 * save hint at the end, because the notebook is where that hint pointed. The note request gets the
 * examples to rewrite; the note itself keeps neither part (spark#1218, "Reihenfolge im Notizbuch").
 */
export function splitImproveAnswer(answer: string, saveHint: string | null): { examples: string; summary: string } {
  const lines = answer.replace(/\r\n/g, "\n").split("\n");
  const rule = lines.findLastIndex((line) => RULE_LINE.test(line));
  if (rule < 0) return { examples: withoutSaveHint(lines.join("\n"), saveHint), summary: "" };
  return {
    examples: lines.slice(0, rule).join("\n").trim(),
    summary: withoutSaveHint(lines.slice(rule + 1).join("\n"), saveHint),
  };
}

// A heading line at any level, the way an example of the compact answer opens.
const HEADING_LINE = /^[ \t]{0,3}#{1,6}[ \t]+\S/;
// The example's number in its heading: the number right after the heading word, the shape
// `### <EXAMPLE> n: <title>` the GROWTH contract asks for, followed by a colon, a stop, a bracket, a dash or
// the end of the heading ("### Example 2: Speaking up", "### **Beispiel 2** – Titel", "### Beispiel 2 (Team):
// X"). A heading without that number keeps every number of its title ("### 5 minutes a day", "### Example:
// 5-minute check").
const HEADING_NUMBER =
  /^([ \t]{0,3}#{1,6}[ \t]+(?:\*\*|__)?\p{L}[\p{L}\p{M}'’ -]*?[ \t\u00A0]+)\d+(?=(?:\*\*|__)?[ \t]*(?:[:.)(\-–—]|$))/u;

/**
 * The examples of an improve answer's example part, one per heading, each from its heading to the next, without
 * the rule lines between them. Text before the first heading is not an example and is not among them.
 */
export function splitImproveExamples(examples: string): string[] {
  const found: string[][] = [];
  for (const line of examples.replace(/\r\n/g, "\n").split("\n")) {
    if (HEADING_LINE.test(line)) found.push([line]);
    // A rule between two examples is layout, not part of either.
    else if (!RULE_LINE.test(line)) found.at(-1)?.push(line);
  }
  return found.map((lines) => lines.join("\n").trim());
}

/**
 * The examples a save chose, for the note request to rewrite: all of them unchanged, or only the chosen ones
 * in their order, numbered 1 to N again under their own titles, so the note model sees exactly the scenes the
 * note will hold (spark#1218). Null when the answer does not hold an example the selection names.
 */
export function selectImproveExamples(examples: string, selection: readonly GrowNoteCount[]): string | null {
  if (selection.length === GROW_NOTE_MAX_ITEMS) return examples;
  const found = splitImproveExamples(examples);
  const chosen = selection.map((index) => found[index - 1]);
  if (chosen.some((example) => example === undefined)) return null;
  return chosen
    .map((example, at) => {
      const [heading, ...rest] = example!.split("\n");
      return [heading!.replace(HEADING_NUMBER, `$1${at + 1}`), ...rest].join("\n");
    })
    .join("\n\n");
}

// The horizontal rule between the note's parts.
const SECTION_RULE = "---";

/**
 * A part the note request wrote, or the definition, without the rule lines it may carry (between two
 * examples, say): the note's rules are its own layout, one between each of its parts and none inside them.
 */
function withoutRules(text: string | null | undefined): string {
  return (text ?? "")
    .split("\n")
    .filter((line) => !RULE_LINE.test(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * The notebook's text: one Markdown note, so Studio reads it as the person's own text and not as a kept
 * prompt. Every piece goes through `escapeMarkers`, so a `<!-- studio:` in an answer cannot turn into a
 * notebook marker. Its parts stand in the order spark#1218 "Reihenfolge im Notizbuch" sets, with a rule
 * between each two: the definition, the growth areas, the examples, the goals, and the summary with the
 * works used. A part with nothing in it is left out with its rule, which is how the growth areas disappear
 * below the completeness bar.
 *
 * Without `parts` it is the note as the save creates it, and as it stays when its own parts are never
 * written: the definition, the growth areas, and the line that the rest could not be written where the
 * examples would stand. The chat's compact examples and its summary never go in: the note shows only the
 * long version, and the chat summary's first sentence would name the growth areas a second time. With
 * `parts` it is the complete note: the reference work's points after the definition, the pattern after the
 * growth areas, the six-part examples instead of the line (one to three, as the save chose), their goals, the
 * sentence on where to start as the summary, and the works used at the end (spark#1218, "Reihenfolge im
 * Notizbuch").
 */
export function composeGrowNotebook(
  draft: GrowNotebookDraft,
  headings: GrowNotebookHeadings,
  parts: GrowNoteParts | null = null,
): string {
  const paragraphs = (...pieces: (string | null | undefined)[]) =>
    pieces.map((piece) => piece?.trim() ?? "").filter(Boolean).join("\n\n");
  const section = (heading: string, body: string) => (body ? `## ${heading}\n\n${body}` : "");
  // Below the bar the draft has neither, and the pattern the request may still have written has nothing
  // to stand after.
  const complete = Boolean(draft.assessment) || draft.growthAreas.length > 0;
  const growthAreas = complete
    ? paragraphs(
        draft.assessment ? `${headings.leadIn} ${draft.assessment.trim()}` : "",
        draft.growthAreas.map((statement) => `- ${statement.trim()}`).join("\n"),
        withoutRules(parts?.growthPattern),
      )
    : "";
  const blocks = [
    section(headings.definition, paragraphs(withoutRules(draft.definition), withoutRules(parts?.referencePoints))),
    section(headings.growthAreas, growthAreas),
    parts ? withoutRules(parts.shortTerm) : headings.notWritten.trim(),
    parts ? withoutRules(parts.longTerm) : "",
    paragraphs(
      section(headings.summary, withoutRules(parts?.start)),
      section(headings.sources, (parts?.sources ?? []).map((title) => `- ${title}`).join("\n")),
    ),
  ];
  return blocks
    .filter(Boolean)
    .map((block) => escapeMarkers(block))
    .join(`\n\n${SECTION_RULE}\n\n`);
}
